import {
    FeatureFlagKeys,
    INotification,
    INotificationScheduling,
    IPublishState,
    NotificationTypeEnum,
    TableKeys
} from '@communication-center/data-objects';
import { editActions } from '../actionTypes';
import { IVisualDesignItem, Notification } from '../../models';
import StepKeys from '../../models/step-keys';
import { ThunkAction } from '../../infrastructure/store.interface';
import { createPublishStates, duplicateNotification as duplicateNotificationUtil } from '../helpers/notifications';
import { GetNotificationByIdQuery, GetNotificationByIdDocument, GetNotificationByIdQueryVariables } from '../../../generated/apollo-hooks';
import logger from '../../services/logger.service';
import { Locations } from '../../models/locations';
import i18n from '../../infrastructure/I18n/i18n';
import { History } from  '../../components/NavigateSetter'

const keyPrefix = 'actions.editNotification';
const t = (key: string, params?: any) => i18n.t(`${keyPrefix}.${key}`, params) as string;

export const _fetchNotificationById = (notificationId: string): ThunkAction => async (dispatch, getState, { apolloClient }) => {
    const { currentSystem, currentEnvironment } = getState().common;

    const { data } = await apolloClient.query<GetNotificationByIdQuery, GetNotificationByIdQueryVariables>({
        query: GetNotificationByIdDocument,
        variables: {
            notificationId,
            systemId: currentSystem,
            environmentId: currentEnvironment?.id
        }
    });

    return data?.getNotificationById?.visualDesignData;
};

const _createEditNotification = (notification: $TSFixMe): ThunkAction => async (dispatch, getState, { featureFlags }): Promise<INotification> => {
    const pushNotificationFF = featureFlags[FeatureFlagKeys.PushNotification];
    const visualDesignData = await dispatch(_fetchNotificationById(notification.id));

    let notificationType = notification.type;

    // Auto convert list notification to Toast / Popup.
    if (notificationType === NotificationTypeEnum.List) {
        notificationType = pushNotificationFF ? NotificationTypeEnum.Toast : NotificationTypeEnum.PopUp;
    }

    return { ...notification, visualDesignData, type: notificationType };
};

export const setNewNotification = (): ThunkAction => (dispatch, getState, { featureFlags }) => {
    const { currentSystem, user, environments } = getState().common;

    const notification: Notification = {
        systemId: currentSystem,
        user: user,
        title: t('title'),
        type: featureFlags[FeatureFlagKeys.PushNotification] ? NotificationTypeEnum.Toast : NotificationTypeEnum.PopUp,
        publishStates: createPublishStates({ environments })
    };

    dispatch({
        type: editActions.SET_EDIT_NOTIFICATION,
        payload: notification
    });
};

export const setNotificationTemplate = (visualDesignItem: Pick<IVisualDesignItem, 'template' | 'id'>): ThunkAction => dispatch =>
    dispatch({
        type: editActions.SET_NOTIFICATION_TEMPLATE,
        payload: { visualDesignItem }
    });

export const setTrackVisualDesignTitle = (shouldTrack: $TSFixMe) => ({
    type: editActions.SET_TRACK_VISUAL_DESIGN_TITLE,
    payload: { shouldTrack }
});

export const setTrackVisualDesignDescription = (shouldTrack: $TSFixMe) => ({
    type: editActions.SET_TRACK_VISUAL_DESIGN_DESCRIPTION,
    payload: { shouldTrack }
});

export const setEditNotification = (notification: Notification): ThunkAction => async dispatch => {
    const payload = await dispatch(_createEditNotification(notification));
    dispatch(setStep(StepKeys.Design));
    dispatch({ type: editActions.SET_EDIT_NOTIFICATION, payload });
    History.navigate()(Locations.Edit);
};

export const duplicateNotification = (notification: Notification, table: TableKeys, notificationsList: $TSFixMe): ThunkAction => async (
    dispatch,
    getState
) => {
    const { user, environments } = getState().common;
    const fullNotification = await dispatch(_createEditNotification(notification) as $TSFixMe);
    const duplicate = duplicateNotificationUtil({ notification: fullNotification, notificationsList, user, environments });
    dispatch(setStep(StepKeys.Design));
    History.navigate()(Locations.Edit);
    dispatch({ type: editActions.DUPLICATE_NOTIFICATION, payload: duplicate });
};

export const notificationPropertyChanged = (changes: Partial<Notification>) => ({
    type: editActions.NOTIFICATION_FIELD_VALUE_CHANGED,
    payload: changes
});

export const publishStatePropertyChanged = (properties: Partial<IPublishState>): ThunkAction => (dispatch, getState) => {
    const environmentId = getState().common?.currentEnvironment?.id;

    dispatch({
        type: editActions.PUBLISHED_STATE_FIELD_VALUE_CHANGED,
        payload: { properties: properties, environmentId: environmentId }
    });
};

export const schedulingPropertyChanged = (properties: Partial<INotificationScheduling>): ThunkAction => (dispatch, getState) => {
    const environmentId = getState().common?.currentEnvironment?.id;

    dispatch({
        type: editActions.SCHEDULING_STATE_FIELD_VALUE_CHANGED,
        payload: { properties: properties, environmentId: environmentId }
    });
};

export const setStep = (step: StepKeys) => ({
    type: editActions.SET_STEP,
    payload: step
});

export const loadVisualDesignsSettings = () => async (dispatch: $TSFixMe) => {
    try {
        const data = await import('../../../assets/visual-designs/visual-designs-w5g.json');
        for (const it of data.categories) {
          const res = await import(`../../../assets/visual-designs${it.icon}`);
          it.icon = res.default;
        }
        for (const it of data.visualDesigns) {
          const res = await import(`../../../assets/visual-designs${it.previewImage}`)
          it.previewImage = res.default;
        }
        data.visualDesigns[0].label = t('loadVisualDesignsSettings.socialUpdate');
        data.visualDesigns[0].description = t('loadVisualDesignsSettings.benefits');
        data.visualDesigns[1].label = t('loadVisualDesignsSettings.businessUpdate');
        data.visualDesigns[1].description = t('loadVisualDesignsSettings.companyAnnouncements');
        data.visualDesigns[2].label = t('loadVisualDesignsSettings.quickUpdate');
        data.visualDesigns[2].description = t('loadVisualDesignsSettings.shortMessages');
        data.visualDesigns[3].label = t('loadVisualDesignsSettings.interactiveUpdate');
        data.visualDesigns[3].description = t('loadVisualDesignsSettings.mediaContent');

        dispatch({
            type: editActions.LOAD_VISUAL_DESIGN_SETTINGS,
            payload: data
        });
    } catch (e: $TSFixMe) {
        logger.error('loadVisualDesignsSettings error', e);
    }
};

export const setShowTemplates = (showTemplates: boolean) => ({
    type: editActions.SET_SHOW_TEMPLATES,
    payload: showTemplates
});

export const setHideTemplatesButton = (hideTemplatesButton: boolean) => ({
    type: editActions.SET_HIDE_TEMPLATES_BUTTON,
    payload: hideTemplatesButton
});
